// @greenlight-spec: 28-it-dashboard-ui.md#internal-health--internal-health-org-admin
import * as React from 'react';

import type { HistoryLine } from './HistoryChart';
import { readout } from './HistoryChart';
import type { HistoryMarker, HistoryPoint } from './historyModel';
import { formatMoment } from './historyTime';

/**
 * The chart's numbers as a table, so no value depends on hovering. Rendered only
 * once opened: a 30-day window is hundreds of rows per chart.
 */
export function HistoryTable({
  title,
  points,
  lines,
  format,
  markers,
}: {
  title: string;
  points: HistoryPoint[];
  /** A column per line, in place of one average and peak. */
  lines?: HistoryLine[];
  format: (value: number) => string;
  markers: (HistoryMarker & { index: number })[];
}): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  // Only when something happened in the window; a column blank on every row reads as broken.
  const showEvents = markers.length > 0;
  return (
    <details
      className="mt-2 text-[11px]"
      onToggle={(event) => setOpen((event.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
        Data table
      </summary>
      {open && (
        <div className="mt-2 max-h-64 overflow-auto">
          <table className="w-full border-collapse text-left">
            <caption className="sr-only">{title}</caption>
            <thead>
              <tr className="text-muted-foreground">
                <th scope="col" className="py-1 pr-3 font-medium">
                  Interval starting
                </th>
                {lines ? (
                  lines.map((line) => (
                    <th key={line.key} scope="col" className="py-1 pr-3 font-medium">
                      {line.label}
                    </th>
                  ))
                ) : (
                  <>
                    <th scope="col" className="py-1 pr-3 font-medium">
                      Average
                    </th>
                    <th scope="col" className="py-1 pr-3 font-medium">
                      Peak
                    </th>
                  </>
                )}
                {showEvents && (
                  <th scope="col" className="py-1 font-medium">
                    Incidents and releases
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="font-mono tabular-nums">
              {points.map((p, i) => {
                const events = markers.filter((m) => m.index === i).map((m) => m.label);
                const observed = p.avg !== null || p.peak !== null;
                return (
                  <tr key={p.t} className="border-t border-border">
                    <td className="py-1 pr-3 font-sans">{formatMoment(p.t)}</td>
                    {lines ? (
                      lines.map((line) => (
                        <td key={line.key} className="py-1 pr-3">
                          {line.points[i] ? readout(line.points[i]!, format) : '—'}
                        </td>
                      ))
                    ) : (
                      <>
                        <td className="py-1 pr-3">
                          {observed && p.avg !== null ? format(p.avg) : '—'}
                        </td>
                        <td className="py-1 pr-3">
                          {observed
                            ? p.peak !== null
                              ? format(p.peak)
                              : '—'
                            : p.noLimit
                              ? 'no limit set'
                              : 'not observed'}
                        </td>
                      </>
                    )}
                    {showEvents && <td className="py-1 font-sans">{events.join('; ')}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </details>
  );
}
